import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { dealLeadsKeys, dealSendBatchesKeys } from '@/hooks/query-keys'
import * as service from '@/services/deal-send-batches.service'
import {
  queuedMessage,
  SEND_TOAST_POSITION,
  SEND_UNDO_MS,
} from '@/features/deal-review/send-batch'
import { extractErrorMessage, toast } from '@/lib/toast'
import type {
  ActiveSendBatches,
  RetrySendBatch,
  SendBatchPreview,
  SendBatchRequest,
} from '@/types/api'

const ACTIVE_POLL_MS = 2_500
// Slow while idle, so a send another user starts still shows up.
const IDLE_POLL_MS = 30_000

function progressOf(active: ActiveSendBatches): string {
  const { running } = active
  return running ? `${running.id}:${running.done}:${running.failed}` : 'idle'
}

function invalidateLeads(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
  void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.counts() })
}

export function useActiveSendBatches() {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: dealSendBatchesKeys.active(),
    // Compared in the fetch, not an effect, so several subscribers still invalidate once.
    queryFn: async () => {
      const previous = queryClient.getQueryData<ActiveSendBatches>(
        dealSendBatchesKeys.active()
      )
      const next = await service.getActiveSendBatches()
      if (previous && progressOf(previous) !== progressOf(next)) {
        invalidateLeads(queryClient)
      }
      return next
    },
    refetchInterval: (query) => {
      const data = query.state.data
      return data && (data.running || data.queued.length > 0)
        ? ACTIVE_POLL_MS
        : IDLE_POLL_MS
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function useRefreshLeads() {
  const queryClient = useQueryClient()
  return () => invalidateLeads(queryClient)
}

const previewQuery = (leads: SendBatchRequest['leads']) => ({
  queryKey: dealSendBatchesKeys.preview(leads.map((lead) => lead.id).sort()),
  queryFn: () => service.previewSendBatch({ leads }),
  retry: false,
})

// Not stale on open: the Send click has just fetched it (useFetchSendBatchPreview).
export function useSendBatchPreview(
  leads: SendBatchRequest['leads'],
  enabled: boolean
) {
  return useQuery({ ...previewQuery(leads), enabled, staleTime: Infinity })
}

export function useFetchSendBatchPreview() {
  const queryClient = useQueryClient()
  return (leads: SendBatchRequest['leads']) =>
    queryClient.fetchQuery({ ...previewQuery(leads), staleTime: 0 })
}

// Batches started by this tab: the status pill opens on a new one and toasts when it finishes.
export const startedBatchIds = new Set<string>()
let startCount = 0
const startListeners = new Set<() => void>()

export function useSendStartCount(): number {
  return useSyncExternalStore(
    (listener) => {
      startListeners.add(listener)
      return () => startListeners.delete(listener)
    },
    () => startCount
  )
}

export function useEnqueueSendBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: service.enqueueSendBatch,
    onSuccess: (result) => {
      if (result.batchId) startedBatchIds.add(result.batchId)
      startCount += 1
      startListeners.forEach((listener) => listener())
      void queryClient.invalidateQueries({
        queryKey: dealSendBatchesKeys.active(),
      })
      invalidateLeads(queryClient)
    },
  })
}

export function useRetrySendBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, scope }: { id: string } & RetrySendBatch) =>
      service.retrySendBatch(id, { scope }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: dealSendBatchesKeys.active(),
      })
      invalidateLeads(queryClient)
    },
  })
}

export function useSendBatchDetail(
  id: string,
  enabled: boolean,
  live: boolean
) {
  return useQuery({
    queryKey: dealSendBatchesKeys.detail(id),
    queryFn: () => service.getSendBatch(id),
    enabled,
    // Reopening the popover on a finished batch must not show the last polled states.
    staleTime: 0,
    refetchInterval: live ? ACTIVE_POLL_MS : false,
  })
}

export interface UndoableSend {
  pending: boolean
  start: (
    leads: SendBatchRequest['leads'],
    preview: SendBatchPreview,
    onQueued: () => void
  ) => void
}

// Page-level, so clearing the selection (which unmounts the bar) neither sends nor cancels.
export function useUndoableSend(): UndoableSend {
  const enqueue = useEnqueueSendBatch()
  const [pending, setPending] = useState(false)
  // Sends the batch now; set only while the undo toast is open.
  const flush = useRef<(() => void) | null>(null)

  // Leaving the page during the undo window sends what the user asked for, rather than dropping it.
  useEffect(() => () => flush.current?.(), [])

  const start: UndoableSend['start'] = (leads, preview, onQueued) => {
    if (flush.current) return
    const label = `Sending ${leads.length} to DealCloud…`
    // One toast id end to end: the undo toast becomes the queued (or error) toast in place.
    const id = toast(label, {
      duration: Infinity,
      position: SEND_TOAST_POSITION,
      action: { label: 'Undo', onClick: () => settle(false) },
      // `!`: sonner's injected styles are unlayered, so they beat Tailwind's utilities layer.
      classNames: { actionButton: 'bg-primary! text-primary-foreground!' },
    })
    const timer = setTimeout(() => settle(true), SEND_UNDO_MS)
    function settle(send: boolean) {
      clearTimeout(timer)
      flush.current = null
      setPending(false)
      if (!send) {
        toast.dismiss(id)
        return
      }
      toast.loading(label, { id, action: undefined })
      // mutateAsync, not mutate: its result still arrives if the page has unmounted.
      enqueue.mutateAsync({ leads }).then(
        (result) => {
          toast.success(queuedMessage(result, preview), {
            id,
            duration: undefined,
          })
          onQueued()
        },
        (error: unknown) =>
          toast.error(
            extractErrorMessage(error) ?? 'The send could not be queued.',
            { id, duration: undefined }
          )
      )
    }
    flush.current = () => settle(true)
    setPending(true)
  }

  return { pending, start }
}
